import { IconPlus } from "@tabler/icons-react";
import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Toast } from "primereact/toast";
import axios from "axios";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import Spinner from "../ogComponent/spinner";

export default function Batches() {
    const navigate = useNavigate();
    const toast = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [courses, setCourses] = useState([]);
    const [formData, setFormData] = useState({
        name: "",
        start_time: dayjs(),
        end_time: dayjs(),
        course_id: "",
        student_capacity: "",
    });
    const [errors, setErrors] = useState({});

    // Access permissions array from Redux store
    const permissions = useSelector((state) => state.emp.permissions);

    const pagePermissions = ["showbatchesID", "addbatchesID"];

    // Fetch batches from API when component mounts
    useEffect(() => {
        const fetchBatches = async () => {
            try {
                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) {
                    throw new Error("Access token missing");
                }
                const response = await axios.get(
                    `${process.env.REACT_APP_API}/api/course/batches/`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                setBatches(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching batches:", error);
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: error.message || "Failed to fetch batches",
                    life: 3000,
                });
                setLoading(false);
            }
        };

        if (permissions.includes("showbatchesID")) {
            fetchBatches();
        } else {
            setLoading(false);
        }
    }, [permissions]);

    // Fetch courses
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) {
                    throw new Error("Access token missing");
                }

                const response = await axios.get(`${process.env.REACT_APP_API}/api/course/combined-courses/`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                setCourses(response.data);
            } catch (error) {
                console.error("Error fetching courses:", error);
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: error.message || "Failed to fetch courses",
                    life: 3000,
                });
            }
        };

        if (permissions.includes("addbatchesID")) {
            fetchCourses();
        }
    }, [permissions]);

    const handleAddBatch = () => {
        if (permissions.includes("addbatchesID")) {
            setShowModal(true);
        }
    };

    const handleCBatchClick = (id) => {
        navigate(`/user/batches/${id}`);
    };

    const handleTimeChange = (name, value) => {
        setFormData({ ...formData, [name]: value });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const validateForm = () => {
        let newErrors = {};
        if (!formData.name) newErrors.name = true;
        if (!formData.start_time) newErrors.start_time = true;
        if (!formData.end_time) newErrors.end_time = true;
        if (!formData.course_id) newErrors.course_id = true;
        if (!formData.student_capacity || formData.student_capacity <= 0 || isNaN(formData.student_capacity)) {
            newErrors.student_capacity = "Student capacity must be a number greater than 0.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!permissions.includes("addbatchesID")) return;

        if (!validateForm()) return;

        try {
            const accessToken = localStorage.getItem("accessToken");
            const transformedData = {
                name: formData.name,
                start_time: dayjs(formData.start_time).format("HH:mm:ss"),
                end_time: dayjs(formData.end_time).format("HH:mm:ss"),
                student_capacity: parseInt(formData.student_capacity),
                course_id: formData.course_id,
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API}/api/course/batches/`,
                transformedData,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            if (response.status === 200 || response.status === 201) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "New Batch Created!",
                    life: 3000,
                });
                // Refresh batches after successful creation
                if (permissions.includes("showbatchesID")) {
                    const newResponse = await axios.get(
                        `${process.env.REACT_APP_API}/api/course/batches/`,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        }
                    );
                    setBatches(newResponse.data);
                }
                setShowModal(false);
                setFormData({
                    name: "",
                    start_time: dayjs(),
                    end_time: dayjs(),
                    course_id: "",
                    student_capacity: ""
                });
                setErrors({});
            }
        } catch (error) {
            console.error("Error submitting batch data:", error);
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: error.message || "Failed to Create Batch",
                life: 3000,
            });
        }
    };

    return (
        <div className="container-fluid px-3 px-md-4 d-flex flex-column gap-3">
            <Toast ref={toast} position="bottom-right" />
            <div className="d-flex flex-column justify-content-start mt-3 mb-1">
                <h5 className="fw-light mb-0">Batches</h5>
                {permissions.includes("showbatchesID") && (
                    <p className="align-self-start mb-0" style={{ color: "grey", fontSize: "clamp(10px, 1.1vw, 12px)", fontWeight: "600" }}>
                        Total {batches.length} Batch{batches.length !== 1 ? "es" : ""}
                    </p>
                )}
            </div>
            {permissions.includes("addbatchesID") && (
                <button className="btn btn-success" style={{ width: "150px" }} onClick={handleAddBatch}>
                    Add New <IconPlus stroke={2} size={20} />
                </button>
            )}

            {loading ? (
                <Spinner />
            ) : permissions.includes("showbatchesID") ? (
                batches.length === 0 ? (
                    <div
                        className="d-flex justify-content-center align-items-end position-relative"
                        style={{ height: "50vh" }}
                    >
                        <div
                            className="position-absolute top-0 start-0 w-100 h-100"
                            style={{
                                backgroundImage: 'url(/assets/file.png)',
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                opacity: 0.3,
                            }}
                        ></div>
                        <p className="m-0 fw-light fs-5 position-relative">No Batches!</p>
                    </div>
                ) : (
                    <div className="row">
                        {batches.map((batch) => (
                            <div
                                key={batch.id}
                                className="col-12 col-sm-6 col-md-6 col-lg-4 mb-3"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleCBatchClick(batch.id)}
                            >
                                <div className="card h-100 px-3 pb-2">
                                    <div className="card-body d-flex flex-column py-1 px-0">
                                        <div className="p-2">
                                            <h6
                                                className="card-title mb-0"
                                                style={{
                                                    fontSize: 'clamp(12px, 1.2vw, 14px)',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: '100%'
                                                }}
                                                title={batch?.name}
                                            >
                                                {batch?.name}
                                            </h6>
                                        </div>
                                        <div
                                            className="rounded-3 px-3 py-2"
                                            style={{ backgroundColor: "#f8f7f7", fontSize: 'clamp(12px, 1.2vw, 14px)' }}
                                        >
                                            <p className="card-text" style={{ marginBottom: '0.5rem' }}>
                                                <i className="bi bi-clock me-2"></i>
                                                Start time: <span className='ms-3'>{batch?.start_time.slice(0, 5)}</span>
                                            </p>
                                            <p className="card-text" style={{ marginBottom: '0.5rem' }}>
                                                <i className="bi bi-clock me-2"></i>
                                                End time: <span className='ms-3'>{batch?.end_time.slice(0, 5)}</span>
                                            </p>
                                            <p className="card-text" style={{ marginBottom: '0.5rem' }}>
                                                <i className="bi bi-people me-2"></i>
                                                Capacity: <span className='ms-3'>{batch?.student_capacity}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : (
                <p>No permission to view batches.</p>
            )}

            {showModal && permissions.includes("addbatchesID") && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add New Batch</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Batch Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`form-control ${errors.name ? "border-danger" : ""}`}
                                        placeholder="Enter batch name"
                                    />
                                </div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="mb-3">
                                        <label className="form-label">Start Time</label>
                                        <TimePicker
                                            value={formData.start_time}
                                            onChange={(value) => handleTimeChange("start_time", value)}
                                            renderInput={(params) => (
                                                <TextField {...params} fullWidth error={!!errors.start_time} />
                                            )}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">End Time</label>
                                        <TimePicker
                                            value={formData.end_time}
                                            onChange={(value) => handleTimeChange("end_time", value)}
                                            renderInput={(params) => (
                                                <TextField {...params} fullWidth error={!!errors.end_time} />
                                            )}
                                        />
                                    </div>
                                </LocalizationProvider>
                                <div className="mb-3">
                                    <label className="form-label mb-0 small">Course</label>
                                    <select
                                        className={`form-select ${errors.course_id ? "border-danger" : ""}`}
                                        name="course_id"
                                        value={formData.course_id || ""}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Course</option>
                                        {courses.map((course) => (
                                            <option key={course.id} value={course.id}>
                                                {course.course_name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.course_id && <span className="text-danger">Required</span>}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Student Capacity</label>
                                    <input
                                        type="number"
                                        name="student_capacity"
                                        value={formData.student_capacity}
                                        onChange={handleInputChange}
                                        className={`form-control ${errors.student_capacity ? "border-danger" : ""}`}
                                        placeholder="Enter student capacity"
                                        min="1"
                                    />
                                    {errors.student_capacity && (
                                        <small className="text-danger">{errors.student_capacity}</small>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleSubmit}>Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}